import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  VStack,
  Text,
  useColorModeValue,
  Checkbox,
  Link as ChakraLink,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  IconButton,
} from '@chakra-ui/react';
import { BsFileLockFill, BsPersonFill, BsEnvelopeFill } from "react-icons/bs";
import { MdClose } from 'react-icons/md';

// Original imports for authentication and API utility
import { isAuth } from '../../util/IsAuth';
import { post } from '../../util/APIHelp';

const RegisterPage: React.FC = () => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [positionTitle, setPositionTitle] = useState(''); // Not used in the provided form, but kept for state
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordConfirmationError, setPasswordConfirmationError] = useState('');
    const [formError, setFormError] = useState('');
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(true); // Kept for consistency, though not directly used for closing the page
    const navigate = useNavigate();

    // Define dynamic colors using the useColorModeValue hook
    const cardBg = useColorModeValue('white', 'gray.800');
    const panelBg = useColorModeValue('gray.100', 'gray.700');
    const inputBg = useColorModeValue('white', 'gray.600');
    const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');

    const validateForm = () => {
        let isValid = true;
        setFirstNameError('');
        setLastNameError('');
        setUsernameError('');
        setEmailError('');
        setPasswordError('');
        setPasswordConfirmationError('');
        setFormError(''); // Clear general form error on new validation

        if(!firstName) {
            setFirstNameError('First name is required.');
            isValid = false;
        }

        if(!lastName) {
            setLastNameError('Last name is required.');
            isValid = false;
        }

        if(!username) {
            setUsernameError('Username is required.');
            isValid = false;
        }

        if(!email) {
            setEmailError('Email is required.');
            isValid = false;
        } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
            setEmailError('Email is invalid.');
            isValid = false;
        }

        if (!password) {
            setPasswordError('Password is required.');
            isValid = false;
        } else if (password.length < 6) {
            setPasswordError('Password must be at least 6 characters.');
            isValid = false;
        } else if (!/[A-Z]/.test(password)) {
            setPasswordError('Password must contain at least one uppercase letter.');
            isValid = false;
        } else if (!/[a-z]/.test(password)) {
            setPasswordError('Password must contain at least one lowercase letter.');
            isValid = false;
        } else if (!/[0-9]/.test(password)) {
            setPasswordError('Password must contain at least one number.');
            isValid = false;
        } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            setPasswordError('Password must contain at least one special character.');
            isValid = false;
        }

        if (!passwordConfirmation) {
            setPasswordConfirmationError('Please confirm your password.');
            isValid = false;
        } else if (password !== passwordConfirmation) {
            setPasswordConfirmationError('Passwords do not match.');
            isValid = false;
        }
        
        // if (!agreeTerms) {
        //     setFormError('You must agree to the terms and conditions.');
        //     isValid = false;
        // }
        
        return isValid;
    };
    

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        if(!validateForm()) {
            return;
        }

        try {
            // Actual API call using the imported 'post' utility
            const response = await post('/register', {
                firstName,
                lastName,
                username,
                email,
                password,
            });

            const data = await response.json();
            if(!response.ok) {
                throw new Error(data.message);
            }

            // localStorage.setItem('token', data.token); // Uncomment if your register API returns a token
            setIsConfirmationModalOpen(true);

        } catch (err) {
            if(err instanceof Error) {
                setFormError(err.message);
            } else {
                setFormError('An unexpected error occurred.');
            }
        }
    };

    const openModal = () => {
        setIsTermsModalOpen(true);
    }

    const closeModal = () => {
        if(isTermsModalOpen) {
            setIsTermsModalOpen(false);
        }
        if(isConfirmationModalOpen) {
            setIsConfirmationModalOpen(false);
            navigate('/login');
        }
    };

    const handleClose = () => {
        // This button is typically used to close a modal or go back.
        // In this context, it will navigate back to the login page.
        navigate('/login');
    };

    return (
        <Flex minH={'100vh'} align={'center'} justify={'center'}>
            <Box
                maxW={'600px'}
                w={'full'}
                bg={cardBg}
                boxShadow={'lg'}
                rounded={'lg'}
                overflow={'hidden'}
                position="relative" // For absolute positioning of close button
            >
                {/* Close Button */}
                <IconButton
                    aria-label="Close"
                    icon={<MdClose/>}
                    onClick={handleClose}
                    variant="ghost"
                    position="absolute"
                    top="1rem"
                    right="1rem"
                    zIndex="1" // Ensure it's above other content
                />

                <VStack spacing={0} align="stretch">
                    {/* Top Panel for Logo */}
                    <Box
                        h={{ base: '150px', md: '200px' }}
                        w={'full'}
                        bgImage={"url('assets/images/jpg/png/two_fish_logo.jpg')"}
                        bgSize={'30%'}
                        bgPos={'center'}
                        bgRepeat={'no-repeat'}
                    />

                    {/* Bottom Panel for Form */}
                    <Box bg={panelBg} w={'full'} p={{ base: 6, md: 8 }}>
                        <Stack align={'center'} mb={6}>
                            <Heading fontSize={{ base: 'xl', md: '2xl' }} color={textColor}>
                                Register for Automation Hub
                            </Heading>
                        </Stack>
                        <form onSubmit={handleSignup}>
                            <VStack spacing={4}>
                                <HStack spacing={{ base: 0, md: 4 }} width="100%" flexDir={{ base: 'column', md: 'row' }}>
                                    <FormControl isRequired isInvalid={!!firstNameError}>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <BsPersonFill color="gray" />
                                            </InputLeftElement>
                                            <Input 
                                                type='text'
                                                placeholder='First Name*'
                                                id="firstName"
                                                value={firstName} 
                                                onChange={(e) => setFirstName(e.target.value)} 
                                                bg={inputBg}
                                            />
                                        </InputGroup>
                                        <FormErrorMessage>{firstNameError}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isRequired isInvalid={!!lastNameError}>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <BsPersonFill color="gray" />
                                            </InputLeftElement>
                                            <Input 
                                                type='text'
                                                placeholder='Last Name*'
                                                id='lastName'
                                                value={lastName} 
                                                onChange={(e) => setLastName(e.target.value)} 
                                                bg={inputBg}
                                            />
                                        </InputGroup>
                                        <FormErrorMessage>{lastNameError}</FormErrorMessage>
                                    </FormControl>
                                </HStack>

                                <HStack spacing={{ base: 0, md: 4 }} width="100%" flexDir={{ base: 'column', md: 'row' }}>
                                    <FormControl isRequired isInvalid={!!usernameError}>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <BsPersonFill color="gray" />
                                            </InputLeftElement>
                                            <Input 
                                                type='text'
                                                placeholder='Username*'
                                                id="username" 
                                                value={username} 
                                                onChange={(e) => setUsername(e.target.value)} 
                                                bg={inputBg}
                                            />
                                        </InputGroup>
                                        <FormErrorMessage>{usernameError}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isRequired isInvalid={!!emailError}>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <BsEnvelopeFill color="gray" />
                                            </InputLeftElement>
                                            <Input 
                                                type="email" 
                                                placeholder='Email*'
                                                id="email" 
                                                value={email} 
                                                onChange={(e) => setEmail(e.target.value)} 
                                                bg={inputBg}
                                            />
                                        </InputGroup>
                                        <FormErrorMessage>{emailError}</FormErrorMessage>
                                    </FormControl>
                                </HStack>

                                <HStack spacing={{ base: 0, md: 4 }} width="100%" flexDir={{ base: 'column', md: 'row' }}>
                                    <FormControl isRequired isInvalid={!!passwordError}>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <BsFileLockFill color="gray" />
                                            </InputLeftElement>
                                            <Input 
                                                type='password'
                                                placeholder='Password*'
                                                id="password" 
                                                value={password} 
                                                onChange={(e) => setPassword(e.target.value)} 
                                                bg={inputBg}
                                            />
                                        </InputGroup>
                                        <FormErrorMessage>{passwordError}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isRequired isInvalid={!!passwordConfirmationError}>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <BsFileLockFill color="gray" />
                                            </InputLeftElement>
                                            <Input
                                                type='password'
                                                placeholder='Retype Password*'
                                                id="passwordConfirmation" 
                                                value={passwordConfirmation} 
                                                onChange={(e) => setPasswordConfirmation(e.target.value)} 
                                                bg={inputBg}
                                            />
                                        </InputGroup>
                                        <FormErrorMessage>{passwordConfirmationError}</FormErrorMessage>
                                    </FormControl>
                                </HStack>
                                
                                {/* Terms and Conditions Checkbox (uncomment if needed) */}
                                {/*
                                <FormControl mt={3}>
                                    <Checkbox isChecked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)}>
                                        I have read the{' '}
                                        <ChakraLink onClick={openModal} color="blue.400">
                                            information page
                                        </ChakraLink>
                                    </Checkbox>
                                </FormControl>
                                */}

                                {formError && <Text color='red.500' textAlign="center">{formError}</Text>}

                                <Button type="submit" colorScheme="blue" width="100%">
                                    Sign Up
                                </Button>

                                <Text align="center">
                                    Already have an account?{' '}
                                    <ChakraLink as={Link} to="/login" color={'blue.400'}>
                                        Login
                                    </ChakraLink>
                                </Text>
                            </VStack>
                        </form>
                    </Box>
                </VStack>
            </Box>

            {/* Terms Modal (uncomment if needed) */}
            {/*
            <Modal isOpen={isTermsModalOpen} onClose={closeModal} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Information Page</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>Directions: <ChakraLink href="https://docs.google.com/document/d/1vz9SnJLdOPHNDyPwGcrb9o0cnJughMSag0KUiB0YGes/edit" isExternal color="blue.400">Google Docs Link</ChakraLink></Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" mr={3} onClick={closeModal}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            */}

            {/* Confirmation Modal */}
            <Modal isOpen={isConfirmationModalOpen} onClose={closeModal} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Registration Successful</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>Your registration was successful. A confirmation email has been sent.</Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" mr={3} onClick={closeModal}>
                            OK
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Flex>
    ); 
}

export default RegisterPage;

